import { ApiClient } from '@api/api-configuration';
import { ServiceDb } from '@api/READ_ONLY/caregiver_api/Api';
import {
  GenericSearchSelect,
  type ApiFetchFunction,
  type ApiQueryParams,
} from '@app/components/ui/GenericSearchSelect';
import { excludeRetiredServices } from '@app/features/services/serviceActivity';
import { SelectProps } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type ServicesSelectProps = {
  onServiceChange?: (service: ServiceDb | ServiceDb[] | undefined) => void;
  placeholder?: string;
  pageSize?: number;
  minSearchLength?: number;
  searchDebounceMs?: number;
  filterCaregiverIds?: number[];
  reloadSignal?: number | string;
  lazyLoadOnOpen?: boolean;
  selectedItemsProvider?: () => ServiceDb | ServiceDb[] | undefined;
  /**
   * Services this picker must not offer, on top of the retired ones it already
   * drops. For a picker that ADDS to a set — a package's contents — the members
   * already in it are not candidates, and offering them means either a duplicate
   * row or a silently ignored pick.
   */
  excludeServiceIds?: readonly number[];
  /** How one service reads in the dropdown. Defaults to its name alone. */
  renderOption?: (service: ServiceDb) => ReactNode;
} & Omit<SelectProps, 'options' | 'onChange' | 'loading' | 'onSearch'>;

export const ServicesSelectGeneric = ({
  onServiceChange,
  placeholder = 'Select Service',
  pageSize = 10,
  minSearchLength = 3,
  searchDebounceMs = 300,
  filterCaregiverIds,
  reloadSignal,
  lazyLoadOnOpen,
  selectedItemsProvider,
  excludeServiceIds,
  renderOption,
  ...selectProps
}: ServicesSelectProps) => {
  const { t } = useTranslation();
  const fetchServices: ApiFetchFunction<ServiceDb> = async (params: ApiQueryParams) => {
    const query = {
      ...params,
      ...(filterCaregiverIds && filterCaregiverIds.length ? { caregiver_ids: filterCaregiverIds } : {}),
    };
    const response = await ApiClient.serviceApi.services.listServices(query);

    // Retired services must not be offered for NEW selection. Filtering here and
    // not in `selectedItemsProvider` is deliberate: a record that already
    // references a since-retired service must keep rendering it, otherwise the
    // line item goes blank. See features/services/serviceActivity.ts.
    //
    // `total` is intentionally left as the server reported it, for the retired rows
    // and for `excludeServiceIds` alike. Correcting it for the removed rows would
    // need a server-side filter, not a client-side guess - and under-reporting it
    // would break the picker's paging.
    const excluded = new Set(excludeServiceIds ?? []);
    const offerable = excludeRetiredServices(response.data.data ?? []).filter(
      (service) => !excluded.has(service.serviceId)
    );

    return {
      ...response,
      data: { ...response.data, data: offerable },
    };
  };
  return (
    <GenericSearchSelect<ServiceDb>
      {...selectProps}
      apiFetch={fetchServices}
      getItemId={(service) => service.serviceId}
      getItemLabel={(service) => service.name}
      onSelectionChange={onServiceChange}
      placeholder={placeholder}
      pageSize={pageSize}
      minSearchLength={minSearchLength}
      searchDebounceMs={searchDebounceMs}
      searchPlaceholder={t('services-select-search-placeholder', { count: minSearchLength })}
      reloadSignal={reloadSignal}
      lazyLoadOnOpen={lazyLoadOnOpen}
      selectedItemsProvider={selectedItemsProvider}
      renderOption={renderOption ?? ((service) => <span>{service.name}</span>)}
    />
  );
};

export default ServicesSelectGeneric;
